<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import MapCategoriesContainer from '@/components/map/MapCategoriesContainer.vue';
import MapDefinition from '@/components/map/MapDefinition.vue';
import FormActions from '@/components/FormActions.vue';
import StandardInput from '@/components/StandardInput.vue';

import { useLanguageValidationErrors } from '@/composables';
import { onBeforeRouteLeave } from 'vue-router';
import { formSaveNavigationGuard, resetFormToItem } from '@/helpers/form.helper';
import { useForm } from 'vee-validate';
import type { FormDto, Locales, StepDto } from '@/services/types/api.types';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useFormsStore } from '@/stores';
import { mapStepValidationSchema } from '@/validation/mapStepSchema';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { provideEditLocale } from '@/composables/data-display-utils';
import type { MapSize, MapStepConfig } from '@/components/map/mapLayer.types';

const props = defineProps<{
  formId: number;
  step: StepDto;
  stepIndex: number;
  languageEnabled: Locales[];
}>();

const { t } = useI18n();

const formContext = useForm({
  validationSchema: mapStepValidationSchema(),
});
const { handleSubmit, errors, meta, resetForm } = formContext;

const formsStore = useFormsStore();

/**
 * The size the consultation page draws a map question at on a desktop, which the boundary canvas is
 * drawn at, so the area the admin frames is the area participants get. The web map is 400px tall (web
 * `Map.vue`, `.base-map`), and 456px wide at any desktop width: the page's column stops at 1100px, and
 * the question column, its card and the map question's padding leave the map 456px of it.
 */
const PARTICIPANT_MAP_SIZE: MapSize = { width: 456, height: 400 };

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const languageErrors = useLanguageValidationErrors(errors);

onBeforeRouteLeave(formSaveNavigationGuard(meta));

const form = useWatchItemLoading<FormDto>(toRef(props, 'formId'), formsStore, (f) => resetFormToItem(formContext, f));

const fieldPrefix = computed(() => `answers.steps[${props.stepIndex}].map`);

const mapConfig = computed<MapStepConfig | null | undefined>(() => form.value?.answers?.steps?.[props.stepIndex]?.map);

const exportInProgress = ref(false);

async function uploadLayer(file: File): Promise<boolean> {
  return await formsStore.uploadMapLayer(props.formId, props.step.id!, { file, version: 'v1' });
}

async function deleteLayer(layerId: number): Promise<boolean> {
  return await formsStore.deleteMapLayer(props.formId, props.step.id!, layerId);
}

const onSubmit = handleSubmit(async (values) => {
  wrapSavingOperation(
    async () => await formsStore.saveForm(values as FormDto),
    () => resetForm({ values: form.value! }),
  );
});

const onCancel = () => {
  if (form.value) {
    resetForm({ values: form.value });
  } else {
    console.warn('onCancel: form is null');
  }
};

async function exportGeoJSON() {
  try {
    exportInProgress.value = true;
    const blob = await formsStore.exportFormGeoJSON(props.formId);
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `form-${props.formId}.geojson`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  } catch (e) {
    console.error(e);
  } finally {
    exportInProgress.value = false;
  }
}
</script>
<template>
  <form-actions
    v-model:editLocale="editLocale"
    :onCancel="onCancel"
    :onSubmit="onSubmit"
    :languageErrors="languageErrors"
    :meta="meta"
    :show-language-switcher="true"
    :language-enabled="languageEnabled"
  />
  <div class="map-settings__wrapper">
    <div class="map-settings">
      <standard-input :name="`${fieldPrefix}.maxVotes`" :label="t('map.maxVotes')" type="number" :min="1" outlined />
      <MapDefinition
        :field-prefix="fieldPrefix"
        :config="mapConfig"
        :edit-locale="editLocale"
        :upload-layer="uploadLayer"
        :delete-layer="deleteLayer"
        :participant-map-size="PARTICIPANT_MAP_SIZE"
      >
        <template #style-actions>
          <div class="row items-center q-gutter-sm">
            <q-btn
              color="primary"
              :loading="exportInProgress"
              :label="$t('map.export.geojson')"
              @click="exportGeoJSON"
            />
          </div>
        </template>
      </MapDefinition>
    </div>
    <div class="map-settings__categories">
      <div class="map-settings__categories-content">
        <MapCategoriesContainer
          :categories="step.map?.categories ?? []"
          :form-id="props.formId"
          :step-id="props.step.id!"
          :languageEnabled="languageEnabled"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.map-settings__wrapper {
  display: flex;
  flex-direction: column;

  .map-settings {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
  }
}
</style>
